import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import { Button, Message } from "rsuite";
import {
  getReservations,
  getReservationsExport,
  repushVcc,
  getDemandClientsByProperty,
  getPromoRatePlansByProperty,
} from "../../../api";
import { formatDate } from "../../../constants/utils";
import { UAL_SOURCES } from "../../../constants/ualActions";
import { requireLoggedInUsername } from "../../../utils/sessionGuard";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { saveFileResponse } from "../../../utils/fileDownload";
import { DEFAULT_PAGE_SIZE } from "../utils";
import { ASIAN_TIMEZONE_MAP, INITIAL_PAGINATION } from "../constants";
import dayjs from "dayjs";

const useReservationData = ({
  selectedProperty,
  formValue,
  filters,
  isEdit,
  isDrawerOpen,
  canRepushVcc,
  columns,
  sortState,
  setSortState,
  // Bumped by useResReportForm on every Search click (see its comment); the
  // fetch effect below keys on it so re-searching unchanged criteria still
  // hits the API instead of silently keeping the stale result set.
  searchTrigger = 0,
}) => {
  const timeZoneRef = useRef("");
  const getReservationReportRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [promoRatePlans, setPromoRatePlans] = useState([]);
  const [demandClients, setDemandClients] = useState([]);
  const [vccRepushed, setVccRepushed] = useState([]);
  const [loadingRepush, setLoadingRepush] = useState(null);
  const [reservationsPage, setReservationsPage] = useState(INITIAL_PAGINATION);
  // Bumped on every report fetch — search, re-search, property switch, sort,
  // page change all funnel through getReservationReport. Consumers key
  // per-row UI state on it (the daily-breakdown expansion collapses on every
  // new result set, since the rows are different records — EO-1895 bug).
  const [searchSequence, setSearchSequence] = useState(0);

  const getFormData = useCallback(() => {
    if (isEdit || isDrawerOpen) return;
    const formData = {
      page: {
        size: DEFAULT_PAGE_SIZE,
        number: reservationsPage.number,
      },
      propertyIds: [selectedProperty?.propertyId],
      timeZone: timeZoneRef.current,
    };

    const {
      lastName,
      firstName,
      htConfirmationCode,
      pmsId,
      sourceRoomConfirmationNumber,
      cancelledReservation,
      checkIn,
      checkOut,
      bookingDate,
    } = formValue;

    if (lastName) formData.guestLastName = lastName;
    if (firstName) formData.guestFirstName = firstName;
    if (htConfirmationCode) formData.confirmationCode = htConfirmationCode;
    if (pmsId) formData.pmsId = pmsId;
    if (sourceRoomConfirmationNumber)
      formData.sourceRoomConfirmationNumber = sourceRoomConfirmationNumber;
    if (cancelledReservation) formData.cancelled = cancelledReservation;

    const dateRanges = [
      [checkIn, "checkinStartDate", "checkinEndDate"],
      [checkOut, "checkoutStartDate", "checkoutEndDate"],
      [bookingDate, "bookingStartDate", "bookingEndDate"],
    ];
    dateRanges.forEach(([value, startKey, endKey]) => {
      if (value) {
        formData[startKey] = formatDate(value[0]);
        formData[endKey] = formatDate(value[1]);
      }
    });

    const filterMappings = [
      [filters.clients, "demandClientIds"],
      [filters.rateplans, "rateplanIds"],
      [filters.roomtypes, "roomTypes"],
      [
        filters.promoRatePlans.filter(
          (id) => !promoRatePlans.find((p) => p.value === id)?.isDerivedRate
        ),
        "dprRateplanIds",
      ],
      [
        filters.promoRatePlans.filter(
          (id) => promoRatePlans.find((p) => p.value === id)?.isDerivedRate
        ),
        "derivedRateIds",
      ],
    ];
    filterMappings.forEach(([value, key]) => {
      if (value.length) formData[key] = value;
    });

    if (sortState.sortBy) {
      formData.sortBy = sortState.sortBy;
      formData.sortDirection = sortState.sortDirection;
    }

    return formData;
  }, [
    isEdit,
    isDrawerOpen,
    formValue,
    reservationsPage.number,
    selectedProperty?.propertyId,
    filters,
    sortState,
    promoRatePlans,
  ]);

  const mapReservationItem = useCallback(
    (item) => {
      const statusClass =
        String(item.status).toLowerCase() === "active" ? "active" : "cancelled";
      const isRepush =
        item.payablePaymentType !== "DIRECT_BILL" && canRepushVcc;
      let repushVccEl = null;
      if (isRepush) {
        repushVccEl = vccRepushed.includes(item.htConfirmationCode) ? (
          <Message className="repush-success" showIcon type="success">
            Requested
          </Message>
        ) : (
          <Button
            className="repush"
            loading={loadingRepush === item.htConfirmationCode}
            onClick={() => setLoadingRepush(item.htConfirmationCode)}
          >
            Repush VCC
          </Button>
        );
      }
      return {
        ...item,
        status: <span className={`status ${statusClass}`}>{item.status}</span>,
        promoRatePlan:
          promoRatePlans.find((promo) => promo.code === item.promoRatePlan)
            ?.label || item.promoRatePlan,
        payable: item.payable ? Number(item.payable).toFixed(2) : "",
        payableTotal: item.payableTotal
          ? Number(item.payableTotal).toFixed(2)
          : "",
        tax: item.tax ? Number(item.tax).toFixed(2) : "",
        statusValue: item.status,
        repushLoading: false,
        repushVcc: repushVccEl,
      };
    },
    [vccRepushed, loadingRepush, promoRatePlans, canRepushVcc]
  );

  const processReservationData = useCallback(
    (result, pageSize) => {
      const data = result.content.map(mapReservationItem);
      data.forEach((item, index) => {
        item.serialNumber = index + 1 + reservationsPage.number * pageSize;
      });
      setReservations(data);
      // An empty result set (e.g. the searched reservation no longer matches
      // the criteria after being cancelled) has no row to read a timezone
      // from — keep the previous suffix rather than throwing into the catch.
      if (data.length > 0) {
        const timezoneId = data[0].timezoneId;
        timeZoneRef.current =
          ASIAN_TIMEZONE_MAP[timezoneId] || dayjs().tz(timezoneId).format("z");
      }
    },
    [mapReservationItem, reservationsPage.number]
  );

  const getReservationReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const formData = getFormData();
      if (!formData) return;
      const response = await getReservations(formData);
      // Bumped only AFTER the read succeeds: the sequence tells consumers
      // "a new result set is on screen". A failed fetch changes nothing on
      // screen, so it must not collapse expanded rows or drop their cache.
      setSearchSequence((sequence) => sequence + 1);
      const result = response.data;
      if (result?.content) {
        processReservationData(result, response.data.page.size);
      }
      if (result?.page) {
        setReservationsPage(result.page);
      }
    } catch (error) {
      // The shared axios client rejects with response bodies / strings, not
      // Error objects — getApiErrorMessage turns any of those shapes into a
      // string react-toastify can actually render.
      toast.error(
        getApiErrorMessage(error, "Failed to load the reservation report")
      );
      getLogger().error("ReservationReport", {
        type: "Error",
        page_url: window.location.href,
        error_desc: "Failed to fetch the reservation report",
        error_stack: error?.stack ?? null,
      });
    } finally {
      setIsLoading(false);
    }
  }, [getFormData, processReservationData]);

  getReservationReportRef.current = getReservationReport;

  const getPromoRateplans = useCallback(async () => {
    try {
      const response = await getPromoRatePlansByProperty(
        selectedProperty?.propertyId
      );
      setPromoRatePlans(
        response.data.data.map((item) => ({
          label: item.name,
          value: item.id,
          code: item.code,
          isDerivedRate: item.isDerivedRate,
        }))
      );
    } catch (error) {
      toast.error(`Failed to fetch Dpr Rateplans: ${error?.message}`);
    }
  }, [selectedProperty?.propertyId]);

  const getDemandClients = useCallback(async () => {
    try {
      const response = await getDemandClientsByProperty(
        selectedProperty?.propertyId
      );
      setDemandClients(
        (response.data.content || []).map((item) => ({
          label: item.demandClient,
          value: Number(item.clientId),
        }))
      );
    } catch (error) {
      getLogger().error("ReservationReport", {
        type: "Error",
        page_url: window.location.href,
        error_desc: "Failed to fetch demand clients",
        error_stack: error?.stack ?? null,
      });
      toast.error(`Failed to fetch Clients`);
    }
  }, [selectedProperty?.propertyId]);

  const exportReservation = async () => {
    setIsExportLoading(true);
    try {
      const formData = getFormData();
      if (!formData) return;
      columns.forEach((column) => {
        if (column.exportKey) {
          formData[column.exportKey] = true;
        }
      });
      const response = await getReservationsExport(formData);

      // No Content-Disposition means the backend queued the export for mail
      // delivery instead of streaming the file back.
      if (!response.headers["content-disposition"]) {
        toast.success("Exported file will be mailed!");
        setIsExportLoading(false);
        return;
      }
      saveFileResponse(response, "reservations.xlsx");
    } catch (error) {
      toast.error(error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleRepushVcc = useCallback(
    async (confirmationCode) => {
      const username = requireLoggedInUsername();
      if (!username) return;
      setLoadingRepush(confirmationCode);
      try {
        await repushVcc(
          confirmationCode,
          username,
          UAL_SOURCES.RESERVATION_REPORT
        );
        toast.success("Successfully repushed");
        setVccRepushed([...vccRepushed, confirmationCode]);
      } catch (error) {
        toast.error(error);
      } finally {
        setLoadingRepush(null);
      }
    },
    [vccRepushed]
  );

  const handleSortChange = useCallback(
    (field) => {
      setSortState((prev) => {
        const isSameField = prev.sortBy === field;
        const newDirection =
          isSameField && prev.sortDirection === "ASC" ? "DESC" : "ASC";
        return { sortBy: field, sortDirection: newDirection };
      });
      setReservationsPage((prev) => ({ ...prev, number: 0 }));
    },
    [setSortState]
  );

  const onPageChange = (page, pageSize) => {
    setReservationsPage((prevState) => ({
      ...prevState,
      number: page - 1,
      size: pageSize,
    }));
  };

  useEffect(() => {
    getPromoRateplans();
    getDemandClients();
  }, [getPromoRateplans, getDemandClients]);

  useEffect(() => {
    if (loadingRepush) {
      handleRepushVcc(loadingRepush);
    }
  }, [loadingRepush, handleRepushVcc]);

  // searchTrigger is deliberately a dependency even though the effect body
  // doesn't read it: a Search click with unchanged criteria changes neither
  // getReservationReport's identity nor isEdit, and without the bump this
  // effect would not re-run — the table kept showing pre-cancellation data
  // until a full page reload (EO-1895). A changed-criteria search bumps the
  // trigger AND the callback identity in the same render, so it still fetches
  // exactly once.
  useEffect(() => {
    if (!isEdit) getReservationReport();
  }, [getReservationReport, isEdit, searchTrigger]);

  return {
    isLoading,
    isExportLoading,
    reservations,
    setReservations,
    promoRatePlans,
    demandClients,
    reservationsPage,
    setReservationsPage,
    searchSequence,
    timeZoneRef,
    getReservationReport,
    getReservationReportRef,
    exportReservation,
    handleSortChange,
    onPageChange,
  };
};

export default useReservationData;
